import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PROMPT_BY_ID } from "@/lib/prompts";
import { PromptList } from "@/components/PromptList";
import { CustomPromptDialog } from "@/components/CustomPromptDialog";
import { PromptRecap } from "@/components/PromptRecap";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function AllPrompts() {
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [custom, setCustom] = useState([]);
  const [folders, setFolders] = useState([]);
  const [usage, setUsage] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPins = () =>
    api
      .get("/prompts/state")
      .then(({ data }) => {
        setPins((data.pinned || []).filter((k) => k.startsWith("tool:")).map((k) => k.slice(5)));
        setUsage(data.usage || {});
      })
      .catch((e) => console.error("could not load pinned prompts", e));

  const loadCustom = () =>
    api
      .get("/prompts/custom")
      .then(({ data }) => {
        setCustom(data.prompts || []);
        setFolders(data.folders || []);
      })
      .catch((e) => console.error("could not load my prompts", e));

  useEffect(() => {
    loadPins();
    loadCustom();
  }, []);

  const togglePin = async (id) => {
    const pinned = !pins.includes(id);
    setPins((list) => (pinned ? [...list, id] : list.filter((x) => x !== id)));
    const name = PROMPT_BY_ID[id]?.label || custom.find((p) => p.id === id)?.label || "That prompt";
    try {
      await api.post("/prompts/pin", { prompt_key: `tool:${id}`, pinned });
      toast.success(pinned ? `${name} pinned to the top` : `${name} unpinned`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not pin that prompt");
      loadPins();
    }
  };

  const reorder = async (ids, move) => {
    setCustom((list) =>
      ids
        .map((id) => list.find((p) => p.id === id))
        .filter(Boolean)
        .map((p) => (move && p.id === move.move_id ? { ...p, folder: move.folder } : p)));
    try {
      await api.post("/prompts/custom/reorder", { ids, ...(move || {}) });
      if (move) {
        toast.success(move.folder ? `Moved to ${move.folder}` : "Moved out of its folder");
        loadCustom();
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that order");
      loadCustom();
    }
  };

  const duplicate = async (p) => {
    try {
      const { data } = await api.post("/prompts/custom", {
        label: `${p.label}`.slice(0, 36) + " ✎",
        ask: p.ask || p.blurb || "What should I know?",
        starter: p.starter,
        icon: p.icon && p.icon !== "Sparkles" ? p.icon : "PenLine",
        blurb: p.blurb || "",
      });
      await loadCustom();
      setEditing(data);
      setDialogOpen(true);
      toast.success(`${p.label} copied — tweak the wording and save`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not copy that prompt");
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/prompts/custom/${p.id}`);
      setCustom((list) => list.filter((x) => x.id !== p.id));
      setPins((list) => list.filter((x) => x !== p.id));
      toast.success(`${p.label} deleted`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete that prompt");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-2 py-6 sm:px-6" data-testid="all-prompts-page">
      <PromptRecap custom={custom} />

      <div className="mb-4 flex justify-end">
        <Button
          data-testid="new-prompt-button"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          className="min-h-[44px] rounded-full bg-brand px-6 text-white hover:bg-brand-hover active:scale-95"
        >
          <Plus className="me-2 h-4 w-4" /> New prompt of my own
        </Button>
      </div>

      <div className="overflow-hidden rounded-3xl border border-white/10 bg-panel">
        <PromptList
          pins={pins}
          custom={custom}
          onTogglePin={togglePin}
          onPick={(id) => navigate(`/chat?prompt=${id}`)}
          onEdit={(p) => {
            setEditing(p);
            setDialogOpen(true);
          }}
          onDelete={remove}
          onDuplicate={duplicate}
          onReorder={reorder}
          usage={usage}
          twoColumns
        />
      </div>

      <CustomPromptDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        folders={folders}
        onSaved={loadCustom}
      />
    </div>
  );
}
